import { inject, Injectable, signal } from '@angular/core';
import { PlayerApiService } from './api/player-api.service';
import { map, Observable } from 'rxjs';
import * as dayjs from 'dayjs';
import { DayJsUtils } from '@hidat/huijs-dates';
import {
  PlayerHost,
  PlayerMix,
  PlayerStation,
  PlayerProgram,
  PlayerUserMix,
  PlayerBrandedTag,
} from '../models';
import { TimelineSegmentItem } from '../services/timeline.service';
import { nString, Uuid } from '@hidat/huijs-common';
import {
  MediaContentType,
  PlayerAutoPlayMode,
  PlayerAutoPlayModes,
} from '@splixer/common';

export interface StationFeatures {
  featured: boolean;
  recents: boolean;
  genres: boolean;
  mixCategories: boolean;
  includeRelatedContent?: boolean;

  defaultSkipSeconds?: number;
  dmcaSkipSeconds?: number;
  autoPlayMode?: PlayerAutoPlayMode;
}
/**
 * Station Service
 * Manages the current station configuration.
 * This takes a PlayerStation object, and loads/builds everything we need from that.
 * This is similar to the 'TenantSettings' class in Heavy Horses.
 */
@Injectable({ providedIn: 'root' })
export class StationManagerService {
  protected api = inject(PlayerApiService);
  public isLoaded = signal(false);
  public isLoading = signal(false);

  public id: Uuid | undefined;
  public tenantId: Uuid | undefined;
  public stationCode = 'Not Loaded';
  public name: string | undefined;
  public tagLine: string | undefined;
  public description: string | undefined;
  public status: number | undefined;
  public accessMode = 0; // Indicates if the station is protected or wide open
  public urlCode = ''; // Code to be used when constructing station specific URL - generally the station code lowercased, ie., /s/kexp/home
  public filtersLoaded = false;

  //@TODO: Add as settings to the station
  public defaultAlbumArtUrl = '/assets/default-track-art.webp';
  public defaultAlbumArtThumbnailUrl =
    '/assets/default-track-art-thumbnail.webp';
  public defaultMixImageUrl = 'assets/default-mix-image.jpg';
  public defaultMixImageThumbnailUrl = 'assets/default-mix-thumbnail.jpg';
  public defaultHostImageUrl: string | undefined;

  // Broadcast
  public programs: PlayerProgram[] = []; // Initial list of programs, for the home page
  public userMixes: PlayerUserMix[] = []; // User mixes
  public mixes: PlayerMix[] = [];
  public featuredMixes: PlayerMix[] = []; // All featured mixes
  public recentMixes: PlayerMix[] = []; // All featured mixes
  public genres: PlayerMix[] = [];
  public hosts: PlayerHost[] = []; // All Hosts
  public mixCategories: PlayerBrandedTag[] = [];
  public includeRelatedContent = true;

  // Player Capabilities
  public defaultSkipSeconds = 30;
  public dmcaSkipSeconds = 600;
  public defaultAutoPlayMode = PlayerAutoPlayModes.OFF;

  /**
   * Load Station
   * Attempts to load the station with the given stationCode from the api.
   * Returns true on success, false if there was a problem.
   * @param stationUid
   * @param features
   */
  public loadStation(
    stationUid: Uuid,
    features: StationFeatures,
  ): Observable<PlayerStation | undefined> {
    this.isLoading.set(true);
    const includes = [
      'hosts[default_mix]',
      'broadcast_programs[host,mix_count,last_show_mix,default_mix]',
    ];
    if (features.featured) {
      includes.push('featured_mixes[program]');
    }
    if (features.recents) {
      includes.push('recent_mixes[program]');
    }
    if (features.genres) {
      includes.push('genres[program]');
    }
    if (features.mixCategories) {
      includes.push('mix_categories');
    }
    if (features.includeRelatedContent != null) {
      this.includeRelatedContent = features.includeRelatedContent;
    }

    if (features.defaultSkipSeconds != null) {
      this.defaultSkipSeconds = features.defaultSkipSeconds;
    }
    if (features.dmcaSkipSeconds != null) {
      this.dmcaSkipSeconds = features.dmcaSkipSeconds;
    }
    if (features.autoPlayMode != null) {
      this.defaultAutoPlayMode = features.autoPlayMode;
    }

    return this.api.stations
      .getOne([stationUid], true, { include: includes.join(',') })
      .pipe(
        map((playerStation) => {
          if (playerStation) {
            this.load(playerStation as PlayerStation);
            this.isLoaded.set(true);
            this.isLoading.set(false);
          }
          return playerStation;
        }),
      ) as Observable<PlayerStation | undefined>;
  }

  /**
   * Loads the station service from the given API hash.
   * @param playerStation The station to load
   */
  protected load(playerStation: PlayerStation | undefined) {
    if (playerStation != null) {
      // Make a shallow copy of the results
      this.filtersLoaded = false;
      this.id = playerStation.uid;
      if (playerStation.stationCode) {
        this.stationCode = playerStation.stationCode;
        this.urlCode = this.stationCode.toLowerCase();
      }
      this.name = playerStation.name;

      this.tagLine = playerStation.tagLine;
      this.description = playerStation.description;
      this.status = playerStation.status;
      this.accessMode = playerStation.accessMode;
      this.filtersLoaded = true;
      this.hosts = playerStation._hosts ?? [];
      this.programs = playerStation._programs ?? [];
      this.mixes = playerStation._mixes ?? [];
      this.genres = playerStation._genres ?? [];
      this.userMixes = playerStation._userMixes ?? [];
      this.featuredMixes = playerStation._featuredMixes ?? [];
      this.recentMixes = playerStation._recentMixes ?? [];
      this.mixCategories = playerStation._mixCategories ?? [];
    }
  }

  public clear() {
    this.isLoaded.set(false);
    this.isLoading.set(false);
    this.filtersLoaded = false;
    this.id = undefined;
    this.stationCode = 'Not Loaded';
    this.urlCode = '';
    this.name = undefined;
    this.tagLine = undefined;
    this.description = undefined;
    this.status = undefined;
    this.accessMode = 0;
    this.filtersLoaded = true;
    this.hosts = [];
    this.programs = [];
    this.mixes = [];
    this.userMixes = [];
    this.featuredMixes = [];
    this.recentMixes = [];
  }

  public formatLongDate(d: dayjs.Dayjs | Date | undefined): string | undefined {
    let ds: string | undefined;
    if (d) {
      const formatter = new Intl.DateTimeFormat([], { dateStyle: 'long' });
      ds = formatter.format(DayJsUtils.toJSDate(d));
    }
    return ds;
  }

  public formatMediumDate(
    d: dayjs.Dayjs | Date | undefined,
  ): string | undefined {
    let ds: string | undefined;
    if (d) {
      const formatter = new Intl.DateTimeFormat([], { dateStyle: 'medium' });
      ds = formatter.format(DayJsUtils.toJSDate(d));
    }
    return ds;
  }

  public formatMediumDateTime(
    d: dayjs.Dayjs | Date | undefined,
  ): string | undefined {
    let ds: string | undefined;
    if (d) {
      const formatter = new Intl.DateTimeFormat([], {
        dateStyle: 'medium',
        timeStyle: 'medium',
      });
      ds = formatter.format(DayJsUtils.toJSDate(d));
    }
    return ds;
  }

  public formatShortDate(
    d: dayjs.Dayjs | Date | undefined,
  ): string | undefined {
    let ds: string | undefined;
    if (d) {
      const formatter = new Intl.DateTimeFormat([], { dateStyle: 'short' });
      ds = formatter.format(DayJsUtils.toJSDate(d));
    }
    return ds;
  }

  public formatTime(d: dayjs.Dayjs | Date | undefined): string | undefined {
    let ds: string | undefined;
    if (d) {
      const formatter = new Intl.DateTimeFormat([], { timeStyle: 'medium' });
      ds = formatter.format(DayJsUtils.toJSDate(d));
    }
    return ds;
  }

  /**
   * Returns the image URL for the given Timeline Segment.
   * By default this will be the associated album artwork, but will degrade as appropriate.
   * @param np TimelineSegment to return image of
   * @param thumbnail
   */
  getTimelineImageUrl(np: TimelineSegmentItem, thumbnail = false) {
    let imageUrl: nString;
    let thumbnailUrl: nString;

    if (np) {
      if (np.content) {
        if (
          np.content.contentType === MediaContentType.MicBreak &&
          np.host?.imageUrl
        ) {
          imageUrl = np.host.imageUrl;
          thumbnailUrl = np.host.branding.thumbnailImageUrl;
        } else if (!np.content.default) {
          // what is default
          imageUrl = np.content.coverArtUrl;
          thumbnailUrl = np.content.coverArtUrl;
        }
        if (!imageUrl) {
          thumbnailUrl = this.defaultAlbumArtThumbnailUrl;
          imageUrl = this.defaultAlbumArtUrl;
        }
      }
      // This will rarely be reached now...
      if (!imageUrl && np.mix) {
        imageUrl = np.mix?.imageUrl;
        thumbnailUrl = np.mix.branding.thumbnailImageUrl;
      }
      if (!imageUrl && np.show) {
        imageUrl = np.show.imageUrl;
        thumbnailUrl = np.show.branding.thumbnailImageUrl;
      }
      if (!imageUrl && np.program) {
        imageUrl = np.program.imageUrl;
        thumbnailUrl = np.program.branding.thumbnailImageUrl;
      }
    }
    if (thumbnail && thumbnailUrl) {
      return thumbnailUrl;
    }
    return imageUrl;
  }

  /**
   * Returns a label that can be used to describe the album associated with this timeline item.
   * @param np
   * @param includePerformer  Should the performer name be appended to the text
   */
  public getTimelineImageLabel(np: TimelineSegmentItem, includePerformer = true) {
    let label = '';
    const content = np.content;
    if (content) {
      if (content.releaseName) {
        label = content.releaseName;
        if (includePerformer && content.performerName) {
          label += ' by ' + content.performerName;
        }
      }
    }
    return label;
  }

  /**
   * Returns the image URL for the given Timeline Segment.
   * @param mix Mix to return image for
   * @param thumbnail
   */
  getMixImageUrl(mix: PlayerMix, thumbnail = false) {
    let imageUrl: nString;
    let thumbnailUrl: nString;
    if (mix) {
      thumbnailUrl = mix.branding.thumbnailImageUrl;
      imageUrl = mix.imageUrl;
      if (!imageUrl) {
        if (mix.sourceType === 'Show' && mix._show) {
          thumbnailUrl = mix._show.branding?.thumbnailImageUrl;
          imageUrl = mix._show.imageUrl;
        } else if (mix.sourceType === 'Host' && mix._host) {
          thumbnailUrl = mix._host.branding?.thumbnailImageUrl;
          imageUrl = mix._host.imageUrl;
        }
      }
      if (!imageUrl && mix._program) {
        thumbnailUrl = mix._program.branding?.thumbnailImageUrl;
        imageUrl = mix._program.imageUrl;
      }
      if (!imageUrl) {
        thumbnailUrl = this.defaultMixImageThumbnailUrl;
        imageUrl = this.defaultMixImageUrl;
      }
    }
    if (thumbnail && thumbnailUrl) {
      return thumbnailUrl;
    }
    return imageUrl;
  }

  /*
    addUserMix(userMix) {
      this.userMixes.push(userMix);
    }
  */
}
